import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  Renderer2,
  SecurityContext,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { IconFieldModule } from 'primeng/iconfield';
import { ImageModule } from 'primeng/image';
import { InputIconModule } from 'primeng/inputicon';
import { MenubarModule } from 'primeng/menubar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, debounceTime, filter, fromEvent } from 'rxjs';
import { SharedModule } from '../../shared/shared.module';

import { customEmailValidator, SiteValidatorService } from '~/shared-common';
import { environment } from '../../../environments/environment';
import { ProductsFrontEndStore } from '../../stores';
import { AuthStore, LocalStorageJwtService } from '../../stores/auth';
import { CloudTagListStore } from '../../stores/cloud-tag/cloud-tag-list.store';

import { OrderDetailStore } from '../../stores/order/order-detail.store';
import { ProductFrontendService } from '../../stores/products/services/products.service';
import { QuotationDetailStore } from '../../stores/quotations/quotation-detail.store';
import { TopbarService } from '../../stores/services/topbar.service';

@Component({
  selector: 'app-topbar',
  templateUrl: './app-topbar.component.html',
  styleUrls: ['./app-topbar.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    MenubarModule,
    AccordionModule,
    AvatarModule,
    BadgeModule,
    ButtonModule,
    CommonModule,
    RouterModule,
    ToastModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    DialogModule,
    DividerModule,
    ImageModule,
    ProgressSpinnerModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopbarComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef;
  @ViewChild('toggleCheckbox', { static: false }) toggleCheckbox!: ElementRef;

  router = inject(Router);
  private productFrontendService = inject(ProductFrontendService);
  private authStore = inject(AuthStore);
  private siteValidatorService = inject(SiteValidatorService);
  private cloudTagListStore = inject(CloudTagListStore);
  private readonly localStorageJwtService = inject(LocalStorageJwtService);
  private domSanitizer = inject(DomSanitizer);
  private productStore = inject(ProductsFrontEndStore);
  private $orderDetailStore = inject(OrderDetailStore);
  private quotationDetailStore = inject(QuotationDetailStore);
  imgSrcUrl: string = `${environment.s3_url}/`;
  login_banner: any =
    'upload/products/5e44704b6803faa43cb6e5c3-1739592891282--1739592891282.png';
  isOnSearch$: any;
  isLoading: boolean = false;
  searchKey$: any;
  $isOnSearch: any = 0;
  showPass: any = 0;
  popupLogin: any = 0;
  popupForgetPassword: any = 0;
  popupRegister: any = 0;
  isLogout: any = 0;
  isWelcome: any = 0;
  showScrollButton = false;
  userPermission: Record<string, any> = {};
  $searchKey: any = '';
  $companyId = computed(() => {
    return Number(this.authStore.user()?.company_id);
  });
  arr_list_company_id: any = [10024, 10026, 10000];
  $allowCompany = computed(() => {
    const companyId = this.$companyId();
    if (this.arr_list_company_id.includes(companyId)) return true;
    return false;
  });
  sortedMenusItems = signal<any[]>([]);

  private fb = inject(FormBuilder);
  $menusItems = this.cloudTagListStore.findFullMenuTagForCompany.records;
  $currentUser = this.authStore.user;
  isNewTheme = computed(() => {
    const currentUser = this.$currentUser();
    return currentUser?.company?.web_theme === 'wt2024_new';
  });
  backToCartLink$ = computed(() => {
    const currentUser = this.$currentUser();
    return currentUser?.company?.web_theme === 'default' ? 'order' : 'orders';
  });

  $totalCount = this.cloudTagListStore.findFullMenuTagForCompany.totalCount;
  $lastedOrder = this.$orderDetailStore.orderDetail;
  $lastedQO = this.quotationDetailStore.lastedQuotationDetail;

  searchControl: FormControl = new FormControl();

  formGroup = this.fb.group({
    username: ['', [Validators.required, Validators.maxLength(255)]],
    password: ['', [Validators.required, Validators.minLength(3)]],
    direct: 0,
  });
  forgetEmailFormGroup = this.fb.group({
    forget_email: [
      '',
      [
        Validators.required,
        Validators.email,
        customEmailValidator(),
        Validators.maxLength(255),
      ],
    ],
  });
  registerFormGroup = this.fb.group({
    user_email: [
      '',
      [
        Validators.required,
        Validators.email,
        customEmailValidator(),
        Validators.maxLength(255),
      ],
    ],
    user_password: ['', [Validators.required, Validators.maxLength(30)]],
    user_first_name: ['', [Validators.required, Validators.maxLength(30)]],
    user_last_name: ['', [Validators.required, Validators.maxLength(30)]],
    user_phone: ['', [Validators.required, Validators.minLength(3)]],
    company_label: [''],
  });

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showScrollButton = window.scrollY > 50; // Show when scrolled down 200px
  }

  // Scroll to top when clicked
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  safeHtml(content: any) {
    return this.domSanitizer.bypassSecurityTrustHtml(content);
  }

  sanitizeInput(input: string): string {
    const strippedInput = this.stripTags(input);
    return this.domSanitizer.sanitize(SecurityContext.URL, strippedInput) || '';
  }

  login() {
    let { username, password, direct } = this.formGroup.value;
    username = username?.trim().toLowerCase();
    const check = this.authStore.login({
      data: { username, password, direct },
    });
  }
  $loggedIn = this.authStore.loggedIn;
  logOut() {
    this.isLogout = true;
    this.$orderDetailStore.resetCurrentOrder();
    this.$orderDetailStore.resetLastOrder();

    this.quotationDetailStore.resetCurrentQO();
    this.quotationDetailStore.resetLastQO();

    this.cloudTagListStore.resetCallState();
    const hostName = this.siteValidatorService.checkGTPSite();
    if (hostName) {
      this.authStore.setLoggedOut(1);
      this.authStore.b2bLogin({
        data: {
          hostName: hostName,
          direct: 1,
        },
      });
    } else {
      this.authStore.setLoggedOut(0);
    }
  }

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private topbarService: TopbarService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {
    effect(
      () => {
        this.init();
        if (!this.$currentUser()?.default_user) {
          this.popupLogin = 0;
          this.cdr.detectChanges();
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }
  resetPassword() {
    let { forget_email } = this.forgetEmailFormGroup.value;
    forget_email = forget_email?.trim().toLowerCase();
    this.authStore
      .forgetPassword({ user_email: forget_email } as any)
      .subscribe({
        next: (result: any) => {
          if (result) {
            this.popupForgetPassword = 0;
            this.showMessage('Reset password is sent', 'info', 'Confirmed');
          } else {
            this.showMessage('Email is not valid', 'error', 'Confirmed');
          }
        },
        error: (error: any) => {},
        complete: () => {},
      });
  }
  errorMessage: string | null = null;
  onSearch() {
    let searchValue: any = this.searchControl.value;
    searchValue = searchValue ? searchValue.trim() : '';
    if (searchValue && searchValue.length >= 2) {
      this.productFrontendService.changeFindKey(searchValue);
      const link = `/product/search/${encodeURIComponent(searchValue)}`;
      if (!this.$isOnSearch[0]) {
        this.router.navigateByUrl(link);
      }
    }
  }
  registerUser() {
    this.isLoading = true;
    this.authStore
      .registerUser([
        {
          user_email: this.registerFormGroup.value.user_email,
          user_password: this.registerFormGroup.value.user_password,
          user_first_name: this.registerFormGroup.value.user_first_name,
          user_last_name: this.registerFormGroup.value.user_last_name,
          user_phone: this.registerFormGroup.value.user_phone,
          company_label: this.registerFormGroup.value.company_label,
        },
      ])

      .subscribe({
        next: (result: any) => {
          if (result._id === '') {
            this.errorMessage =
              'This email is already registered, please try a different email.';
            this.isLoading = false;
            this.cdr.detectChanges();
          } else {
            this.popupLogin = 0;
            if (result.user_status) {
              const data: any = {
                username: this.registerFormGroup.value.user_email,
                password: this.registerFormGroup.value.user_password,
                direct: 0,
              };
              this.showMessage(
                `Welcome ${this.registerFormGroup.value.user_email}`,
                'info',
                'Confirmed',
              );

              this.authStore.login({
                data: data,
              });
              this.isLoading = false;
              this.handleDialogHide();
            } else {
              this.popupRegister = 0;
              this.popupLogin = 0;
              this.isWelcome = 1;
              this.isLoading = false;
            }
          }
        },

        error: (error: any) => {},
        complete: () => {},
      });
  }
  handleDialogHide() {
    this.renderer.removeClass(document.body, 'ui-overflow-hidden');
    this.renderer.setStyle(document.body, 'overflow', 'auto');
  }
  clearErrorMessage() {
    this.errorMessage = null;
  }
  callRegister() {
    if (
      this.$currentUser()?.company_id == '10026'
      // || this.$currentUser()?.company_id == '10019'
    ) {
      this.popupLogin = 0;
      this.popupRegister = 0;
      this.popupForgetPassword = 0;
      this.router.navigateByUrl('resources/register');
    } else {
      this.popupLogin = 0;
      this.popupRegister = 1;
      this.popupForgetPassword = 0;
    }
  }
  callLogin() {
    this.popupLogin = 1;
    this.popupRegister = 0;
    this.popupForgetPassword = 0;
  }
  forgotPassword() {
    this.popupRegister = 0;
    this.popupLogin = 0;
    this.popupForgetPassword = 1;
  }
  init() {
    if (
      this.$currentUser() &&
      this.$currentUser()?.company &&
      this.$currentUser()?.company?.login_banner
    ) {
      this.login_banner = this.$currentUser()?.company?.login_banner;
    }
    if (this.$currentUser()?._id && !this.$totalCount()) {
      const params: any = [
        this.$currentUser()?.company_id,
        this.$currentUser()?.company?.use_company_data ?? 0,
        this.$currentUser(),
      ];
      this.cloudTagListStore.getMenuData(params);
    }
    this.userPermission = this.$currentUser()?.user_permission ?? {};
    // this.searchControl.setValue(this.$searchKey);
  }
  ngOnInit() {
    this.router.events.subscribe(() => (this.showIconField = false));

    this.init();
    this.topbarService.registerCalled$.subscribe(() => this.callRegister());
    this.topbarService.signedInCallSource$.subscribe(() => this.callLogin());
    this.topbarService.forgotPasswordCalled$.subscribe(() =>
      this.forgotPassword(),
    );
    const menusItems = this.$menusItems();
    if (menusItems) {
      this.sortedMenusItems.set(this.sortMenusItems(menusItems));
    }

    this.searchKey$ = combineLatest(this.productFrontendService.findKey$);

    if (
      this.$searchKey == '' &&
      this.localStorageJwtService.getItemSTR('currentSearch')
    ) {
      this.$searchKey = this.localStorageJwtService.getItemSTR('currentSearch');
      this.searchControl.setValue(this.$searchKey);
    }
    this.searchKey$.subscribe((response: any) => {
      if (response != '') {
        this.$searchKey = response;
      }
      if (this.$searchKey == '') {
        this.searchControl.reset();
      }
    });

    this.isOnSearch$ = combineLatest(this.productFrontendService.onSearch$);
    this.isOnSearch$.subscribe((response: any) => {
      this.$isOnSearch = response;
      if (!this.$isOnSearch[0]) {
        this.updateValue('');
      }
    });
  }

  updateValue(newValue: string) {
    this.localStorageJwtService.setItemSTR('currentSearch', newValue);
    this.searchControl.setValue(newValue);
  }
  showMessage(message: any, severity: any, label: any) {
    this.messageService.add({
      severity: severity,
      summary: label,
      detail: message,
    });
  }
  stripTags(input: string): string {
    const div = document.createElement('div');
    div.innerHTML = input;
    return div.textContent || div.innerText || '';
  }
  switchNameOrder(fullName: string): string {
    const [lastName = '', firstName = ''] = fullName.split(' ');
    return `${firstName} ${lastName}`;
  }
  getInitials(fullName: string) {
    const nameParts = fullName.split(' ');
    return nameParts.map((part) => part[0]).join('');
  }
  displayImage(image_file: any) {
    if (!image_file || image_file == '') return '';
    if (image_file[0] == '/') {
      image_file = image_file.substring(1);
    }
    return this.imgSrcUrl + image_file;
  }
  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (
      this.dropdownMenu &&
      this.toggleCheckbox &&
      !this.dropdownMenu.nativeElement.contains(event.target) &&
      !this.toggleCheckbox.nativeElement.contains(event.target)
    ) {
      this.closeDropdown();
    }
  }
  closeDropdown() {
    this.toggleCheckbox.nativeElement.checked = false;
  }

  sortMenusItems(items: any[]): any[] {
    return items.slice().sort((a, b) => a.title.localeCompare(b.title));
  }

  expandedIndex: number | null = null;

  errorMesage = '';
  showIconField = false;
  isDropdownOpen: boolean = false;
  showMobileMenu: boolean = false;

  // Function to toggle the mobile menu visibility
  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  closeMobileMenu() {
    this.showMobileMenu = false;
    this.expandedIndex = null;
  }

  // Toggles the submenu for a specific parent item
  toggleSubmenu(index: number) {
    this.expandedIndex = this.expandedIndex === index ? null : index;
    this.isDropdownOpen = false;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
    this.expandedIndex = null;
  }

  ngAfterViewInit() {
    if (!this.searchInput) {
      return;
    }
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        debounceTime(300),
        filter(() => this.searchControl.dirty),
      )
      .subscribe(() => {
        let searchQuery: any = this.sanitizeInput(this.searchControl.value);
        const checkSearchQuery = searchQuery ? searchQuery.trim() : '';
        if (checkSearchQuery && checkSearchQuery.length >= 2) {
          this.productStore.resetSearchBoardPageNumber(1);
          this.updateValue(searchQuery);
          this.productFrontendService.changeFindKey(checkSearchQuery);

          const link = `/product/search/${encodeURIComponent(checkSearchQuery)}`;
          if (!this.$isOnSearch[0]) {
            this.router.navigateByUrl(link);
          }
        } else if (!checkSearchQuery) {
          this.updateValue('');
          this.productFrontendService.changeFindKey('');
          if (this.$isOnSearch[0]) {
            this.router.navigateByUrl('/');
          }
        }
      });
  }
}
